import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { TASK1_AREAS } from '../data/sprintActivities';

export default function SprintTask1() {
  const navigate = useNavigate();
  const [sprint, setSprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/sprint/status');
        if (!data.sprint) { navigate('/sprint'); return; }
        setSprint(data.sprint);
        setNote(data.sprint.task1?.noteForNitin || '');
      } catch { navigate('/sprint'); }
      setLoading(false);
    })();
  }, [navigate]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(f.type)) {
      setError('Please upload a PDF or Word document.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('File must be under 5 MB.');
      return;
    }
    setError('');
    setFile(f);
  };

  const handleSaveDraft = async () => {
    try {
      const { data } = await api.post('/sprint/task1/save-draft', { noteForNitin: note });
      setSprint(data.sprint);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save draft.');
    }
  };

  const handleSubmit = async () => {
    if (!file && !sprint.task1?.fileUrl) {
      setError('Please upload your one A4 page.');
      return;
    }
    setUploading(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      fd.append('noteForNitin', note);
      const { data } = await api.post('/sprint/task1/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSprint(data.sprint);
      setSuccess('Task 1 submitted successfully! Nitin will review it before your Zoom session.');
      setFile(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
        </div>
      </div>
    );
  }

  const status = sprint?.task1?.status || 'not_started';
  const isApproved = status === 'approved';
  const isSubmitted = status === 'submitted' || status === 'under_review';
  const needsRevision = status === 'needs_revision';
  const canUpload = !isApproved && !isSubmitted;

  return (
    <div style={s.page}>
      {/* Header */}
      <button onClick={() => navigate('/sprint')} style={s.backBtn}>&larr; Back to Sprint</button>

      <div style={s.hero}>
        <p style={s.tagline}>SPRINT — TASK 1</p>
        <h1 style={s.title}>Know Your Law School</h1>
        <p style={s.subtitle}>Complete within 2 months of starting Sprint</p>
        <p style={s.deadlineText}>
          Deadline: {new Date(sprint.task1Deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Status badge */}
      {isApproved && (
        <div style={s.approvedBanner}>Task 1 has been approved by Nitin.</div>
      )}
      {isSubmitted && (
        <div style={s.submittedBanner}>Your submission is with Nitin. He will review it before your Zoom session.</div>
      )}
      {needsRevision && (
        <div style={s.revisionBanner}>
          <p style={{ margin: 0, fontWeight: '600' }}>Revision requested</p>
          {sprint.task1.reviewNote && <p style={{ margin: '6px 0 0', fontSize: '13px' }}>{sprint.task1.reviewNote}</p>}
        </div>
      )}

      {/* Instructions */}
      <div style={s.instructionCard}>
        <p style={{ ...s.bodyText, fontWeight: '600', color: 'var(--navy)', marginBottom: '10px' }}>The Six Areas to Explore</p>
        <p style={s.bodyText}>Visit each area, speak to the relevant people, and write one A4 page covering all six. Include what you found, one thing that surprised you, and one action you will take.</p>
        <div style={s.areaList}>
          {TASK1_AREAS.map((area, i) => (
            <div key={i} style={s.areaItem}>
              <span style={s.areaNum}>{i + 1}</span>
              <div>
                <p style={{ ...s.bodyText, fontWeight: '600', color: 'var(--navy)', marginBottom: '2px' }}>{area.title}</p>
                <p style={{ ...s.bodyText, color: 'var(--text-2)', fontSize: '12px' }}>{area.shortDesc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submission area */}
      <div style={s.submitCard}>
        <p style={{ ...s.bodyText, fontWeight: '700', color: 'var(--navy)', marginBottom: '14px' }}>Your Submission</p>

        {/* Existing file */}
        {sprint.task1?.fileName && (
          <div style={s.existingFile}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <span style={{ fontSize: '13px', color: 'var(--text)' }}>{sprint.task1.fileName}</span>
            {sprint.task1.fileUrl && (
              <a href={sprint.task1.fileUrl} target="_blank" rel="noopener noreferrer" style={s.downloadLink}>Download</a>
            )}
          </div>
        )}

        {/* Upload input */}
        {canUpload && (
          <>
            <label style={s.uploadLabel}>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <div style={s.uploadArea}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>
                  {file ? file.name : 'Click to upload PDF or Word document (max 5 MB)'}
                </p>
              </div>
            </label>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={handleSaveDraft}
              placeholder="Add a short note for Nitin (optional)"
              maxLength={500}
              rows={3}
              style={s.textarea}
            />
            <p style={s.charCount}>{note.length}/500</p>
          </>
        )}

        {/* Read-only note for submitted/approved */}
        {!canUpload && sprint.task1?.noteForNitin && (
          <div style={{ marginTop: '10px' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-3)', fontWeight: '600', marginBottom: '4px' }}>Your note to Nitin:</p>
            <p style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.6' }}>{sprint.task1.noteForNitin}</p>
          </div>
        )}

        {error && <p style={s.error}>{error}</p>}
        {success && <p style={s.success}>{success}</p>}

        {canUpload && (
          <button
            onClick={handleSubmit}
            disabled={uploading}
            className="primary-btn"
            style={s.submitBtn}
          >
            {uploading ? 'Uploading...' : (needsRevision ? 'Re-submit Task 1' : 'Submit Task 1')}
          </button>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { maxWidth: '720px', margin: '0 auto', padding: '32px 20px 40px' },
  loadingWrap: { display: 'flex', justifyContent: 'center', padding: '80px 0' },
  spinner: { width: '28px', height: '28px', border: '3px solid var(--border)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  backBtn: { background: 'none', border: 'none', color: 'var(--text-2)', fontSize: '13px', cursor: 'pointer', padding: '0', marginBottom: '16px', fontWeight: '500' },
  hero: { marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--border-2)' },
  tagline: { fontSize: '11px', fontWeight: '700', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '8px' },
  title: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '28px', fontWeight: '700', color: 'var(--navy)', marginBottom: '6px', lineHeight: '1.2' },
  subtitle: { fontSize: '14px', color: 'var(--text-2)', marginBottom: '4px' },
  deadlineText: { fontSize: '13px', color: 'var(--gold)', fontWeight: '600' },
  approvedBanner: { background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px', fontWeight: '600' },
  submittedBanner: { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px' },
  revisionBanner: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '14px' },
  instructionCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px' },
  bodyText: { fontSize: '14px', color: 'var(--text)', lineHeight: '1.7', margin: 0 },
  areaList: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '14px' },
  areaItem: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  areaNum: { width: '24px', height: '24px', borderRadius: '50%', background: 'var(--gold-light)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', flexShrink: 0, border: '1px solid var(--gold-mid)' },
  submitCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 24px' },
  existingFile: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '14px' },
  downloadLink: { marginLeft: 'auto', fontSize: '12px', fontWeight: '600', color: 'var(--navy)', textDecoration: 'underline' },
  uploadLabel: { display: 'block', cursor: 'pointer', marginBottom: '14px' },
  uploadArea: { border: '2px dashed var(--border)', borderRadius: '10px', padding: '24px', textAlign: 'center', transition: 'border-color 0.2s' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box' },
  charCount: { fontSize: '11px', color: 'var(--text-3)', textAlign: 'right', margin: '4px 0 0' },
  error: { color: 'var(--danger)', fontSize: '13px', marginTop: '10px' },
  success: { color: 'var(--success)', fontSize: '13px', marginTop: '10px' },
  submitBtn: { width: '100%', padding: '13px', fontSize: '14px', fontWeight: '700', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', marginTop: '16px' },
};
